import {
  Box,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import {
  useEditor,
  useFieldErrorTranslation,
} from "../../../../../common/editing/save-coordinator";
import { ThemedDateTimePicker } from "../../../../../common/input/DateTimeSelectors/ThemedDateTimePicker";

interface BidSubmissionDeadlineFieldProps {
  eventId: string;
  currentDeadline: string | null;
  hideLabel?: boolean;
}

const FIELD_PATH = "eventData.bidSubmissionDeadline";
const END_OF_DAY = { hours: 23, minutes: 59 };

export const BidSubmissionDeadlineField = ({
  eventId,
  currentDeadline,
  hideLabel = false,
}: BidSubmissionDeadlineFieldProps) => {
  const { t } = useTranslation();
  const { updateField, fieldStatus, fieldError } = useEditor<
    SourcingEventV2Dto,
    SourcingEventV2PatchDto
  >(eventId);
  const translateFieldError = useFieldErrorTranslation();

  const error = fieldError(FIELD_PATH);
  const status = fieldStatus(FIELD_PATH);
  // The picker resolves times in the buyer's browser zone; name it explicitly rather than
  // implying a fixed zone the value isn't actually pinned to.
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleChange = (iso?: string) => {
    updateField({ eventData: { bidSubmissionDeadline: iso ?? null } });
  };

  return (
    <FormControl isInvalid={Boolean(error)} isDisabled={status === "saving"}>
      {!hideLabel && <FormLabel htmlFor="bidSubmissionDeadline">{t("Bid deadline")}</FormLabel>}
      <ThemedDateTimePicker
        id="bidSubmissionDeadline"
        ariaLabel={hideLabel ? (t("Bid deadline") ?? undefined) : undefined}
        date={currentDeadline}
        onChange={handleChange}
        defaultTime={END_OF_DAY}
      />
      {/* Reserve the error/helper vertical space so toggling validity never shifts the timeline below. */}
      <Box minH="2.75rem" mt="1">
        {error ? (
          <FormErrorMessage mt="0">{translateFieldError(error)}</FormErrorMessage>
        ) : (
          <FormHelperText mt="0">
            {t("Tender deadline timezone: {{timezone}}", { timezone })}
          </FormHelperText>
        )}
      </Box>
    </FormControl>
  );
};
